import React, { useState } from 'react';
import { Card, Button, Typography, Descriptions, Tag, message, Modal, Table, Divider, Alert } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined, EyeOutlined, ThunderboltOutlined, FileAddOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// Special passthrough template identifier
const PASSTHROUGH_TEMPLATE_KEY = '__passthrough__';

const TemplateSelection = ({ templates, selectedTemplate, onTemplateSelect, passthroughEnabled = false }) => {
    const [loading, setLoading] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    const handleTemplateSelect = async (templateKey) => {
        setLoading(true);

        try {
            const template = { ...templates[templateKey], templateKey }; // Include templateKey in the template object
            onTemplateSelect(template);
            message.success(`Selected template: ${template.name}`);
        } catch (error) {
            message.error('Failed to select template');
        } finally {
            setLoading(false);
        }
    };

    const handlePassthroughSelect = () => {
        setLoading(true);
        try {
            const passthroughTemplate = {
                templateKey: PASSTHROUGH_TEMPLATE_KEY,
                name: 'Passthrough (Custom Upload)',
                description: 'Upload file as-is with custom filename and optional column renaming',
                filename_template: 'custom_upload',
                isPassthrough: true,
                fields: {} // No predefined fields for passthrough
            };
            onTemplateSelect(passthroughTemplate);
            message.success('Selected Passthrough mode');
        } catch (error) {
            message.error('Failed to select passthrough mode');
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewTemplate = (templateKey) => {
        const template = templates[templateKey];
        setPreviewTemplate(template);
        setPreviewModalVisible(true);
    };

    const getFieldTypeColor = (type) => {
        const colors = {
            string: 'blue',
            number: 'green',
            date: 'orange',
            boolean: 'purple',
        };
        return colors[type] || 'default';
    };

    const getRequiredColor = (required) => {
        return required ? 'red' : 'green';
    };

    const getRequiredText = (required) => {
        return required ? 'Required' : 'Optional';
    };

    const isPassthroughSelected = selectedTemplate?.isPassthrough === true;

    return (
        <div>
            <Title level={4}>Select Data Template</Title>
            <Text type="secondary">
                Choose the appropriate template for your data. Each template defines the expected structure and field requirements.
            </Text>

            {/* Passthrough Option */}
            {passthroughEnabled && (
                <>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ThunderboltOutlined style={{ color: '#faad14' }} />
                                <span style={{ color: '#faad14' }}>Passthrough Mode (Custom Upload)</span>
                            </div>
                        }
                        extra={
                            <Button
                                size="small"
                                type={isPassthroughSelected ? 'default' : 'primary'}
                                icon={isPassthroughSelected ? <CheckCircleOutlined /> : <FileAddOutlined />}
                                onClick={handlePassthroughSelect}
                                loading={loading}
                                disabled={isPassthroughSelected}
                                style={{ 
                                    background: isPassthroughSelected ? undefined : '#faad14',
                                    borderColor: isPassthroughSelected ? undefined : '#faad14'
                                }}
                            >
                                {isPassthroughSelected ? 'Selected' : 'Use Passthrough'}
                            </Button>
                        }
                        style={{ 
                            marginTop: 24, 
                            marginBottom: 16,
                            borderColor: isPassthroughSelected ? '#52c41a' : '#faad14',
                            borderWidth: isPassthroughSelected ? 2 : 1
                        }}
                    >
                        <Alert
                            message="Direct Upload Mode"
                            description={
                                <div>
                                    <p style={{ marginBottom: 8 }}>
                                        Upload your file directly without mapping to a predefined template. Perfect for:
                                    </p>
                                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                        <li>One-time data uploads</li>
                                        <li>Custom file formats</li>
                                        <li>Files that don't match existing templates</li>
                                    </ul>
                                    <p style={{ marginTop: 8, marginBottom: 0 }}>
                                        You can customize the output filename and rename columns before uploading.
                                    </p>
                                </div>
                            }
                            type="warning"
                            showIcon
                            icon={<ThunderboltOutlined />}
                        />
                    </Card>

                    <Divider>Or select a predefined template</Divider>
                </>
            )}

            <div style={{ marginTop: passthroughEnabled ? 16 : 24 }}>
                {Object.entries(templates).map(([templateKey, template]) => (
                    <Card
                        key={templateKey}
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <InfoCircleOutlined />
                                {template.name}
                            </div>
                        }
                        extra={
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => handlePreviewTemplate(templateKey)}
                                >
                                    Preview
                                </Button>
                                <Button
                                    size="small"
                                    type={selectedTemplate?.name === template.name ? 'default' : 'primary'}
                                    icon={selectedTemplate?.name === template.name ? <CheckCircleOutlined /> : null}
                                    onClick={() => handleTemplateSelect(templateKey)}
                                    loading={loading}
                                    disabled={selectedTemplate?.name === template.name}
                                >
                                    {selectedTemplate?.name === template.name ? 'Selected' : 'Select'}
                                </Button>
                            </div>
                        }
                        style={{ marginBottom: 16 }}
                    >
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Description">
                                {template.description}
                            </Descriptions.Item>
                            <Descriptions.Item label="Filename Template">
                                <Text code>{template.filename_template}</Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <div style={{ marginTop: 16 }}>
                            <Title level={5}>Required Fields:</Title>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {Object.entries(template.fields).map(([fieldKey, field]) => (
                                    <div key={fieldKey} style={{ marginBottom: 8 }}>
                                        <Tag color={getRequiredColor(field.required)}>
                                            {getRequiredText(field.required)}
                                        </Tag>
                                        <Tag color={getFieldTypeColor(field.type)}>
                                            {field.type}
                                        </Tag>
                                        <Text strong>{field.name}</Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {field.description}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {selectedTemplate && (
                <Card
                    title={
                        <span>
                            {selectedTemplate.isPassthrough ? (
                                <><ThunderboltOutlined style={{ color: '#faad14', marginRight: 8 }} />Selected Mode</>
                            ) : (
                                'Selected Template'
                            )}
                        </span>
                    }
                    style={{ marginTop: 24, border: `2px solid ${selectedTemplate.isPassthrough ? '#faad14' : '#52c41a'}` }}
                >
                    <Descriptions column={1}>
                        <Descriptions.Item label="Name">
                            <Text strong>{selectedTemplate.name}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Description">
                            {selectedTemplate.description}
                        </Descriptions.Item>
                        {!selectedTemplate.isPassthrough && (
                            <>
                                <Descriptions.Item label="Total Fields">
                                    {Object.keys(selectedTemplate.fields).length}
                                </Descriptions.Item>
                                <Descriptions.Item label="Required Fields">
                                    {Object.values(selectedTemplate.fields).filter(field => field.required).length}
                                </Descriptions.Item>
                            </>
                        )}
                        {selectedTemplate.isPassthrough && (
                            <Descriptions.Item label="Mode">
                                <Tag color="gold">Passthrough - No field validation</Tag>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </Card>
            )}

            <Modal
                title={`Template Preview: ${previewTemplate?.name || ''}`}
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setPreviewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {previewTemplate && (
                    <div>
                        <Descriptions column={1} style={{ marginBottom: 24 }}>
                            <Descriptions.Item label="Name">
                                <Text strong>{previewTemplate.name}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Description">
                                {previewTemplate.description}
                            </Descriptions.Item>
                            <Descriptions.Item label="Filename Template">
                                <Text code>{previewTemplate.filename_template}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Fields">
                                {Object.keys(previewTemplate.fields).length}
                            </Descriptions.Item>
                            <Descriptions.Item label="Required Fields">
                                {Object.values(previewTemplate.fields).filter(field => field.required).length}
                            </Descriptions.Item>
                        </Descriptions>

                        <Title level={5}>Field Details</Title>
                        <Table
                            dataSource={Object.entries(previewTemplate.fields).map(([key, field]) => ({
                                key,
                                fieldKey: key,
                                name: field.name,
                                description: field.description,
                                type: field.type,
                                required: field.required
                            }))}
                            columns={[
                                {
                                    title: 'Field Name',
                                    dataIndex: 'name',
                                    key: 'name',
                                    render: (text, record) => (
                                        <div>
                                            <Text strong>{text}</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {record.fieldKey}
                                            </Text>
                                        </div>
                                    )
                                },
                                {
                                    title: 'Description',
                                    dataIndex: 'description',
                                    key: 'description',
                                    ellipsis: true
                                },
                                {
                                    title: 'Type',
                                    dataIndex: 'type',
                                    key: 'type',
                                    render: (type) => (
                                        <Tag color={getFieldTypeColor(type)}>
                                            {type}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Required',
                                    dataIndex: 'required',
                                    key: 'required',
                                    render: (required) => (
                                        <Tag color={getRequiredColor(required)}>
                                            {getRequiredText(required)}
                                        </Tag>
                                    )
                                }
                            ]}
                            pagination={false}
                            size="small"
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default TemplateSelection;
